/**
 * QualityScore — a mono numeric score next to a mini progress bar (Claude Design
 * `.qscore` / `.qbar`). The bar fill is `ok` by default; pass a `tone` to recolor
 * (e.g. `warn` / `danger` for low scores).
 *
 *   <QualityScore value={0.92} />            → "92" + a 92%-filled bar
 *   <QualityScore value={87} max={100} />    → "87" + an 87%-filled bar
 */
import type { HTMLAttributes, ReactElement } from 'react';

import { cn } from './cn.js';

export type QualityScoreTone = 'ok' | 'warn' | 'danger' | 'info' | 'primary';

export interface QualityScoreProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** The score. If `<= 1` it is treated as a fraction (×100 for display). */
  value: number;
  /** Upper bound for the bar fill (defaults to 100, or 1 for fractional input). */
  max?: number;
  /** Bar fill color (default `ok`). */
  tone?: QualityScoreTone;
  /** Override the displayed number (defaults to the rounded percentage). */
  label?: string;
}

const fillTone: Record<QualityScoreTone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  primary: 'bg-primary',
};

export function QualityScore({
  value,
  max,
  tone = 'ok',
  label,
  className,
  ...props
}: QualityScoreProps): ReactElement {
  const bound = max ?? (value <= 1 ? 1 : 100);
  const pct = Math.max(0, Math.min(100, (value / bound) * 100));
  const display = label ?? String(Math.round(value <= 1 ? value * 100 : value));

  return (
    <span
      className={cn(
        'mono inline-flex items-center gap-1.5 text-[11.5px] font-bold tabular-nums',
        className,
      )}
      {...props}
    >
      {display}
      <span
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-1 w-[34px] overflow-hidden rounded-full bg-surface-3"
      >
        <span
          className={cn('block h-full rounded-full', fillTone[tone])}
          style={{ width: `${pct}%` }}
        />
      </span>
    </span>
  );
}
